import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import type { Bucket } from '@storeflare/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState, Page } from '@/components/Page';
import { api, WebApiError } from '@/lib/api';
import { validateBucketName } from '@/lib/validation';

export function BucketsPage() {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const buckets = useQuery({
    queryKey: ['buckets'],
    queryFn: () => api<Bucket[]>('/v1/buckets'),
  });
  const create = useMutation({
    mutationFn: (body: { name: string }) =>
      api<Bucket>('/v1/buckets', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: (b) => {
      qc.invalidateQueries({ queryKey: ['buckets'] });
      toast.success(`Bucket "${b.name}" created`);
    },
    onError: (err: unknown) => {
      const message =
        err instanceof WebApiError
          ? extractFieldMessage(err) ?? err.message
          : 'Failed to create bucket';
      toast.error(message);
    },
  });

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    const error = validateBucketName(trimmed);
    if (error) {
      toast.error(error);
      return;
    }
    await create.mutateAsync({ name: trimmed });
    setName('');
  }

  return (
    <Page
      title="Buckets"
      subtitle="R2 buckets your tokens can write to."
      actions={
        <form onSubmit={onCreate} className="flex gap-2" noValidate>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="bucket-name"
            className="w-56"
          />
          <Button type="submit" disabled={create.isPending}>
            Create
          </Button>
        </form>
      }
    >
      {buckets.isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : !buckets.data || buckets.data.length === 0 ? (
        <EmptyState title="No buckets yet" hint="Create one to start uploading." />
      ) : (
        <Card>
          <CardContent className="divide-y p-0">
            {buckets.data.map((b) => (
              <Link
                key={b.id}
                to={`/buckets/${b.id}`}
                className="flex items-center justify-between px-4 py-3 transition-colors hover:bg-muted/50"
              >
                <div>
                  <div className="font-medium">{b.name}</div>
                  <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{b.region}</span>
                    <Badge variant={b.public_access ? 'destructive' : 'secondary'}>
                      {b.public_access ? 'public' : 'private'}
                    </Badge>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span>{new Date(b.created_at).toLocaleDateString()}</span>
                  <ChevronRight className="h-4 w-4" />
                </div>
              </Link>
            ))}
          </CardContent>
        </Card>
      )}
    </Page>
  );
}

// Pulls the first field-level message out of the server's validation envelope
// (set by httpErrors.validation in apps/api/src/lib/errors.ts). Returns null
// when the error isn't a validation error or has no field detail.
function extractFieldMessage(err: WebApiError): string | null {
  const fields = err.details?.['fields'];
  if (!Array.isArray(fields) || fields.length === 0) return null;
  const first = fields[0] as { message?: unknown };
  return typeof first?.message === 'string' ? first.message : null;
}
